import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import joi from 'joi';
import { first } from 'rxjs/operators';
import { JoiValidationOptionsConstant } from '@vivli/shared/infrastructure/constants';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { useDataRequestsApiContext, useToastApiContext } from '@vivli/core/infrastructure/context';
import { AddRequestedStudyDuringDraftView } from '@vivli/features/studies/views';

const addRequestedStudySchema = joi
    .object({
        studyId: joi.string().required().label('Study ID')
    })
    .options(JoiValidationOptionsConstant);

const defaultValues = {
    studyId: ''
};

export interface AddRequestedStudyDuringDraftModalComponentProps {
    dataRequestId: string;
    onClose: () => void;
    onStudyAdded: (dataRequest?: IDataRequest) => void;
}

export const AddRequestedStudyDuringDraftModalComponent = ({
    dataRequestId,
    onClose,
    onStudyAdded
}: AddRequestedStudyDuringDraftModalComponentProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dataRequestsService = useDataRequestsApiContext();
    const toastService = useToastApiContext();

    const {
        control,
        handleSubmit,
        setError,
        formState: { isValid, errors }
    } = useForm({
        mode: 'all',
        resolver: joiResolver(addRequestedStudySchema),
        reValidateMode: 'onChange',
        defaultValues
    });

    const onSubmit = (formValues: { studyId: string }) => {
        setIsSubmitting(true);
        dataRequestsService
            .addListedStudyByIdToDataRequest({
                studySponsorIdOrNctId: formValues.studyId,
                dataRequestId
            })
            .pipe(first())
            .subscribe({
                next: (result) => {
                    if (result.item2 === 'Success') {
                        onStudyAdded(result.item1);
                        toastService.info(`Study with ID ${formValues.studyId} is successfully added to the data request.`);
                        onClose();
                    } else if (result.item2 === 'Success-External') {
                        onStudyAdded(result.item1);
                        toastService.info(
                            `Study with ID ${formValues.studyId}, successfully added to the data request, is provisioned on
                            a Vivli partner platform. You will need to create a separate data request on the partner platform.`,
                            { autoClose: 10000 }
                        );
                        onClose();
                    } else {
                        const instructions =
                            result.item2.indexOf('is not listed on the platform') !== -1 ||
                            result.item2.indexOf('is already included in the data request') !== -1
                                ? ''
                                : 'Please click the Enquiry button below to ask about its availability';
                        setError('studyId', { message: `${result.item2}${instructions}` });
                    }
                },
                error: () => {
                    setError('studyId', { message: `Error getting study with ID ${formValues.studyId}` });
                    setIsSubmitting(false);
                },
                complete: () => {
                    setIsSubmitting(false);
                }
            });
    };

    return (
        <Controller
            name="studyId"
            control={control}
            render={({ field }) => (
                <AddRequestedStudyDuringDraftView
                    studyIdValue={field.value}
                    onStudyIdChange={field.onChange}
                    studyIdError={errors.studyId?.message}
                    studyIdLabel="Enter NCT ID or Sponsor Protocol ID"
                    onSubmitClick={handleSubmit(onSubmit)}
                    isSubmitDisabled={!isValid}
                    isLoading={isSubmitting}
                    enquiriesUrl="/admin/enquiries"
                    onCancelClick={onClose}
                />
            )}
        />
    );
};
